import { Fragment } from 'react';
import { parseInlineMarkdown } from '@orborix/shared-types';

/**
 * Renderer markdown-lite para la "Explicación expandida" de una corrección.
 *
 * El backend (`explainCorrectionText`, niveles 2+) devuelve texto con un subset
 * acotado de markdown — líneas `- ` (bullets) y `**negrita**` inline — para que
 * la explicación no sea un muro de texto. Acá lo parseamos a bloques y lo
 * pintamos. Deliberadamente NO es un parser de markdown completo (KISS): sólo
 * párrafos, listas de bullets y negrita inline, que es lo único que el prompt
 * habilita. La negrita inline reusa `parseInlineMarkdown` (`@orborix/shared-types`,
 * misma util que el karaoke del coach) para no duplicar el emparejado de `**`.
 */

export type ExplanationBlock =
  | { type: 'p'; text: string }
  | { type: 'ul'; items: string[] };

/**
 * Una línea es un bullet si (tras trim) arranca con `-`, `*` o `•` seguido de
 * espacio + contenido, o es sólo el marcador (bullet vacío → string vacío).
 * Devuelve el contenido del bullet, o `null` si no es un bullet. No confunde un
 * guión pegado a una palabra (`-word`, no-bullet) con un bullet.
 */
function bulletText(line: string): string | null {
  const m = /^(?:[-*•])(?:\s+(.*))?$/.exec(line);
  return m ? (m[1] ?? '').trim() : null;
}

/**
 * Parsea el texto a bloques: agrupa líneas de bullets consecutivas en una `ul`
 * y el resto en párrafos. Las líneas en blanco separan bloques. Función pura
 * (testeable) — no toca React.
 */
export function parseExplanationBlocks(text: string): ExplanationBlock[] {
  const blocks: ExplanationBlock[] = [];
  let bullets: string[] = [];
  const flushBullets = (): void => {
    if (bullets.length > 0) {
      blocks.push({ type: 'ul', items: bullets });
      bullets = [];
    }
  };

  for (const rawLine of text.split('\n')) {
    const line = rawLine.trim();
    if (line === '') {
      flushBullets();
      continue;
    }
    const bt = bulletText(line);
    if (bt !== null) {
      if (bt !== '') bullets.push(bt);
      continue;
    }
    flushBullets();
    blocks.push({ type: 'p', text: line });
  }
  flushBullets();
  return blocks;
}

/** Texto con negrita inline (`**...**`) → spans. */
function InlineBold({ text }: { text: string }) {
  const { clean, bold } = parseInlineMarkdown(text);
  if (bold.length === 0) return <>{clean}</>;
  const out: React.ReactNode[] = [];
  let cursor = 0;
  bold.forEach(([start, end], i) => {
    if (start > cursor) out.push(<Fragment key={`t${i}`}>{clean.slice(cursor, start)}</Fragment>);
    out.push(<strong key={`b${i}`}>{clean.slice(start, end)}</strong>);
    cursor = end;
  });
  if (cursor < clean.length) out.push(<Fragment key="tail">{clean.slice(cursor)}</Fragment>);
  return <>{out}</>;
}

export function ExplanationContent({ text }: { text: string }) {
  const blocks = parseExplanationBlocks(text);
  return (
    <>
      {blocks.map((b, i) =>
        b.type === 'ul' ? (
          <ul className="err-expand-ul" key={i}>
            {b.items.map((it, j) => (
              <li key={j}>
                <InlineBold text={it} />
              </li>
            ))}
          </ul>
        ) : (
          <p className="err-expand-p" key={i}>
            <InlineBold text={b.text} />
          </p>
        ),
      )}
    </>
  );
}
